/**
 * ObjectDeleteConfirm — Blender-style delete confirmation popover for object mode.
 *
 * Pressing X in object mode opens this single-item popover (title "Delete?",
 * one option "Delete") rather than deleting immediately. Matches Blender's
 * confirmation step to prevent accidental deletion.
 */

import { useCallback } from "react";
import { selectionToLeafObjectIds } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import type { CommandMenuOption } from "./FloatingCommandMenu.tsx";
import { FloatingCommandMenu } from "./FloatingCommandMenu.tsx";
import { useOperatorMenu } from "./hooks/useOperatorMenu.ts";

const OPTIONS: CommandMenuOption[] = [
  { id: "delete", label: "Delete", shortcutKey: "d" },
];

export function ObjectDeleteConfirm() {
  const menu = useOperatorMenu("objectDelete");

  const handleSelect = useCallback(
    (id: string) => {
      if (id === "delete") {
        const s = useStore.getState();
        const selected = selectionToLeafObjectIds(
          s.blendFiles,
          s.collections,
          s.selectedObjectTargets,
        );
        if (selected.size > 0) {
          const sceneIds = new Set(s.sceneObjects.keys());
          const blendEntriesToRemove = new Set<string>();
          for (const [entryId, entry] of s.blendFiles) {
            for (const obj of entry.file.objects) {
              if (selected.has(obj.name)) {
                blendEntriesToRemove.add(entryId);
                break;
              }
            }
          }
          for (const objId of selected) {
            if (sceneIds.has(objId)) s.removeSceneObject(objId);
          }
          for (const entryId of blendEntriesToRemove) {
            s.removeBlendFile(entryId);
          }
        }
      }
      menu.close();
    },
    [menu],
  );

  return (
    <FloatingCommandMenu
      open={menu.open}
      onClose={menu.close}
      title="Delete?"
      anchorPoint={menu.anchorPoint}
      options={OPTIONS}
      onSelect={handleSelect}
      panelKey="object_delete"
    />
  );
}
